import React, { useEffect, useMemo, useRef } from 'react';
import { Box } from '../../../CommonComponents/OPL/Box/Box';
import { Text } from '../../../CommonComponents/OPL/Text/Text';
import { Input } from '../../../CommonComponents/OPL/Input/Input';
import { Checkbox } from '../../../CommonComponents/OPL/Checkbox/Checkbox';
import { UserActivityFilterOption } from '../../UserActivity.meta';

interface FilterOptionListProps {
  title: string;
  options: UserActivityFilterOption[];
  selectedIds: string[];
  searchTerm: string;
  debouncedSearchTerm: string;
  onSearchChange: (value: string) => void;
  onSelectedIdsChange: (ids: string[]) => void;
  searchSelectedIds: string[];
  onSearchSelectedIdsChange: (ids: string[]) => void;
}

export const FilterOptionList = ({
  title,
  options,
  selectedIds,
  searchTerm,
  debouncedSearchTerm,
  onSearchChange,
  onSelectedIdsChange,
  searchSelectedIds,
  onSearchSelectedIdsChange,
}: FilterOptionListProps) => {
  const filteredOptions = useMemo(
    () =>
      options.filter(option =>
        option.label.toLowerCase().includes(debouncedSearchTerm.toLowerCase()),
      ),
    [options, debouncedSearchTerm],
  );

  const selectedIdsRef = useRef(selectedIds);
  const searchSelectedIdsRef = useRef(searchSelectedIds);
  const prevDebouncedSearchTermRef = useRef(debouncedSearchTerm);
  const prevFilteredIdsRef = useRef<string[]>([]);

  selectedIdsRef.current = selectedIds;
  searchSelectedIdsRef.current = searchSelectedIds;

  // Search selection sync rules:
  // - Entering search: every visible result is selected, regardless of prior choices.
  // - While searching: newly visible results are auto-selected; still-visible results
  //   keep the user's check/uncheck (do NOT re-select all on every query change).
  // - Clearing search: merge in-scope search picks into selectedIds; out-of-scope ids
  //   keep their pre-search state (do NOT remove every searchSelectedIds from selectedIds).
  useEffect(() => {
    const filteredIds = filteredOptions.map(option => option.id);
    const prevTerm = prevDebouncedSearchTermRef.current;
    const wasSearching = prevTerm !== '';
    const isSearching = debouncedSearchTerm !== '';

    if (!isSearching && wasSearching) {
      const searchScope = new Set(prevFilteredIdsRef.current);
      const outOfScope = selectedIdsRef.current.filter(id => !searchScope.has(id));
      const inScope = searchSelectedIdsRef.current.filter(id => searchScope.has(id));
      onSelectedIdsChange([...outOfScope, ...inScope]);
      onSearchSelectedIdsChange([]);
    } else if (isSearching && !wasSearching) {
      onSearchSelectedIdsChange([...filteredIds]);
      onSelectedIdsChange(
        [...selectedIdsRef.current, ...filteredIds].filter(
          (item, index, arr) => arr.indexOf(item) === index,
        ),
      );
    } else if (isSearching && wasSearching) {
      const prevFilteredSet = new Set(prevFilteredIdsRef.current);
      const newlyVisible = filteredIds.filter(id => !prevFilteredSet.has(id));
      const keptSearchSelected = searchSelectedIdsRef.current.filter(id =>
        filteredIds.includes(id),
      );

      onSearchSelectedIdsChange([...keptSearchSelected, ...newlyVisible]);

      if (newlyVisible.length > 0) {
        onSelectedIdsChange(
          [...selectedIdsRef.current, ...newlyVisible].filter(
            (item, index, arr) => arr.indexOf(item) === index,
          ),
        );
      }
    }

    prevDebouncedSearchTermRef.current = debouncedSearchTerm;
    prevFilteredIdsRef.current = filteredIds;
  }, [debouncedSearchTerm, filteredOptions, onSelectedIdsChange, onSearchSelectedIdsChange]);

  const allSelected =
    options.length > 0 && options.every(option => selectedIds.includes(option.id));

  const allSearchSelected =
    filteredOptions.length > 0 &&
    filteredOptions.every(option => searchSelectedIds.includes(option.id));

  const toggleOptionId = (id: string) => {
    onSelectedIdsChange(
      selectedIds.includes(id) ? selectedIds.filter(x => x !== id) : [...selectedIds, id],
    );
    if (debouncedSearchTerm) {
      onSearchSelectedIdsChange(
        searchSelectedIds.includes(id)
          ? searchSelectedIds.filter(x => x !== id)
          : [...searchSelectedIds, id],
      );
    }
  };

  const handleSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onSelectedIdsChange(e.target.checked ? options.map(option => option.id) : []);
  };

  const handleSearchSelectAllChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      const searchFilteredIds = filteredOptions.map(option => option.id);
      const combinedIds = [...selectedIds, ...searchFilteredIds];
      onSearchSelectedIdsChange([...searchFilteredIds]);
      onSelectedIdsChange(combinedIds.filter((item, index) => combinedIds.indexOf(item) === index));
    } else {
      onSearchSelectedIdsChange([]);
      onSelectedIdsChange(selectedIds.filter(id => !searchSelectedIds.includes(id)));
    }
  };

  if (options.length === 0) {
    return null;
  }

  return (
    <Box as="section" p="12" minWidth={0} aria-label={title}>
      <Input
        value={searchTerm}
        onChange={evt => onSearchChange(evt.target.value)}
        leftIcon="far fa-search"
        placeholder="Search"
        aria-label={`Search ${title}`}
      />

      {filteredOptions.length > 0 ? (
        <Box mt="12">
          {debouncedSearchTerm === '' ? (
            <Checkbox my={6} checked={allSelected} onChange={handleSelectAllChange}>
              (Select All)
            </Checkbox>
          ) : (
            <Checkbox my={6} checked={allSearchSelected} onChange={handleSearchSelectAllChange}>
              (Select All Searched Results)
            </Checkbox>
          )}
          {filteredOptions.map(option => (
            <Checkbox
              key={option.id}
              my={6}
              isOverflowWrap
              checked={
                debouncedSearchTerm !== ''
                  ? searchSelectedIds.includes(option.id)
                  : selectedIds.includes(option.id)
              }
              onChange={() => toggleOptionId(option.id)}
            >
              {option.label}
            </Checkbox>
          ))}
        </Box>
      ) : (
        <Text as="p" fontWeight={500} mt="12">
          No results found
        </Text>
      )}
    </Box>
  );
};
